import { Link } from '@tanstack/react-router';
import { TICKET_BOARD_RESOLVED_DAYS } from '@servwell/shared';
import { ScrollStrip } from '../ui/ScrollStrip';
import {
  ReplyDue,
  TicketPriorityBadge,
  TicketSourceChip,
  ticketRowLabel,
} from './TicketBits';
import { hiddenCount, moreInList, stateLabel, type TicketFilters } from './ticketView';
import type { TicketColumn, TicketRow } from './types';

/**
 * The Board (spec §5): a column per status, each the server's first `TICKET_BOARD_CAP` cards
 * (`ticket.board`), and "N more" sending the office to the List filtered to that status.
 *
 * ⭐ The columns scroll SIDEWAYS inside their own strip (`ScrollStrip`: a fade and a chevron at
 * an end with more past it, UX baseline), never the page; each column scrolls DOWN inside
 * itself, so the strip, and its chevrons, stay a screen tall however long a column is. A
 * vertical wheel scrolls the strip sideways, except over a column that can still scroll that
 * way, which takes it (`innerScrollTakesWheel`).
 *
 * No hooks of its own (the strip has its own), so a test can render it.
 */
export function TicketBoard({
  columns,
  filters,
  openId,
  onOpen,
  tz,
  now,
}: {
  columns: TicketColumn[];
  filters: TicketFilters;
  /** The ticket whose panel is open: its card is marked and tinted. */
  openId: string | undefined;
  onOpen: (ticketId: string) => void;
  tz: string;
  now: number;
}) {
  return (
    <ScrollStrip>
      <div className="flex min-w-max gap-3 pb-2" data-probe="ticket-board">
        {columns.map((col) => {
          const more = hiddenCount(col.total, col.rows.length);
          const headingId = `ticket-column-${col.status}`;
          return (
            <section
              key={col.status}
              aria-labelledby={headingId}
              data-column={col.status}
              className="flex w-[272px] shrink-0 flex-col rounded-[var(--radius)] border border-border bg-surface"
            >
              <div className="flex items-baseline justify-between gap-2 border-b border-border px-3 py-2">
                <h2 id={headingId} className="text-sm font-semibold">
                  {stateLabel(col.status)}
                  {col.status === 'resolved' ? (
                    <span className="ml-1 font-normal text-muted-foreground">
                      · last {TICKET_BOARD_RESOLVED_DAYS} days
                    </span>
                  ) : null}
                </h2>
                <span className="tabular-nums text-xs text-muted-foreground">
                  {col.total}
                </span>
              </div>
              <ul className="max-h-[min(65dvh,44rem)] space-y-2 overflow-y-auto p-2">
                {col.rows.length === 0 ? (
                  <li className="py-3 text-center text-xs text-muted-foreground">
                    Nothing here
                  </li>
                ) : (
                  col.rows.map((t) => (
                    <li key={t.id}>
                      <BoardCard
                        ticket={t}
                        open={t.id === openId}
                        onOpen={onOpen}
                        tz={tz}
                        now={now}
                      />
                    </li>
                  ))
                )}
              </ul>
              {more > 0 ? (
                <div className="border-t border-border px-3 py-2">
                  <Link
                    to="/tickets"
                    search={moreInList(filters, col.status)}
                    data-more={col.status}
                    className="text-sm font-medium text-[var(--sw-brand-text)] underline-offset-2 hover:underline"
                  >
                    {col.status === 'resolved'
                      ? 'See all resolved'
                      : `${more} more in the list`}
                  </Link>
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </ScrollStrip>
  );
}

function BoardCard({
  ticket: t,
  open,
  onOpen,
  tz,
  now,
}: {
  ticket: TicketRow;
  open: boolean;
  onOpen: (ticketId: string) => void;
  tz: string;
  now: number;
}) {
  return (
    <button
      type="button"
      data-ticket-key={t.id}
      aria-current={open ? 'true' : undefined}
      aria-haspopup="dialog"
      aria-label={ticketRowLabel(t)}
      onClick={() => onOpen(t.id)}
      className={`w-full rounded-[var(--radius-sm)] border p-2 text-left text-sm hover:bg-surface-2 ${
        open ? 'border-brand bg-[var(--sw-brand-pill)]' : 'border-border'
      }`}
    >
      <span className="flex items-baseline justify-between gap-2">
        <span className="flex items-center gap-2">
          <span className="font-mono text-xs text-muted-foreground">#{t.number}</span>
          <TicketSourceChip source={t.source} />
        </span>
        <TicketPriorityBadge priority={t.priority} />
      </span>
      <span className="mt-1 block truncate font-medium">{t.title}</span>
      <span className="block truncate text-xs text-muted-foreground">
        {t.customer.displayName}
      </span>
      <span className="mt-1 flex min-w-0 items-baseline justify-between gap-2 text-xs">
        <span className="min-w-0 truncate text-muted-foreground">
          {t.owner ? (t.owner.name ?? 'Former staff') : 'Nobody yet'}
        </span>
        <span className="shrink-0">
          <ReplyDue ticket={t} tz={tz} now={now} />
        </span>
      </span>
    </button>
  );
}
